/**
 * In-app navigation. `navigateTo` swaps the URL via history.pushState and asks
 * the entry-point router (main.tsx) to re-render the matching screen in place,
 * so moving between the lobby, matchmaking, deck pages and the catalog never
 * reloads the document (no bootstrap spinner chain between screens).
 */

import { useSyncExternalStore, type MouseEvent } from 'react';

type NavigationListener = () => void;

const listeners = new Set<NavigationListener>();

/**
 * The shell's scrollable region. Scrolling does NOT happen on the window:
 * `.auth-app-shell` is `height: 100dvh; overflow: hidden` and its content row
 * owns the scrollbar. That row survives route swaps, so its scrollTop carries
 * over unless it is reset explicitly.
 */
const SCROLL_ROOT_SELECTOR = '[data-app-scroll-root]';

/** Subscribe the entry-point router. Returns an unsubscribe. */
export function onNavigate(listener: NavigationListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * A newly opened screen must start at the top. Applied twice: once now, for
 * the case where the swap already landed, and once on the next frame, because
 * `root.render()` schedules the swap instead of applying it synchronously and
 * a taller incoming screen would otherwise keep the outgoing scroll offset.
 */
function resetScroll(): void {
  const apply = (): void => {
    for (const root of document.querySelectorAll<HTMLElement>(SCROLL_ROOT_SELECTOR)) {
      root.scrollTop = 0;
    }
  };
  apply();
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(apply);
}

function subscribePath(listener: () => void): () => void {
  const unsubscribe = onNavigate(listener);
  window.addEventListener('popstate', listener);
  return () => {
    unsubscribe();
    window.removeEventListener('popstate', listener);
  };
}

/** The current pathname, re-read on in-app navigation and history moves. */
export function useCurrentPath(): string {
  return useSyncExternalStore(subscribePath, () => window.location.pathname, () => '/');
}

/** Navigate to an in-app path without reloading the document. */
export function navigateTo(path: string): void {
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const samePath = path === current;
  if (!samePath) window.history.pushState(null, '', path);
  for (const listener of [...listeners]) listener();
  // Re-clicking the current entry is a no-op, not a fresh screen: keep the
  // reader where they were.
  if (!samePath) resetScroll();
}

/**
 * `onClick` for any in-app `<a href>`. The href stays real — middle-click, Ctrl
 * or Cmd click, "open in new tab" and the status-bar preview all keep working —
 * and only a plain left-click is turned into a `navigateTo`.
 *
 * Every in-app link needs this. An `<a href>` left unhandled reloads the
 * document, which throws away the running app and replays the whole cold boot:
 * blank page, ~420kB of JS re-parsed, all styles re-injected, the web font
 * swapping in a second time. That is the most violent thing the UI can do, and
 * next to it no amount of transition polish is noticeable.
 */
export function handleInAppLinkClick(event: MouseEvent<HTMLAnchorElement>): void {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
  ) return;
  event.preventDefault();
  navigateTo(event.currentTarget.getAttribute('href') ?? '/');
}
